import React, { useEffect } from "react";
import { Physics } from "@react-three/rapier";
import { Perf } from "r3f-perf";
import { Grid, KeyboardControls } from "@react-three/drei";
import Ecctrl, { useGame } from "../../src/Ecctrl";
import MapRenderer from "./mapRenderer";
import { useLevelProgress } from "../stores/useLevelProgress";
import { useControls } from "leva";
import { useDialogs } from "../stores/useDialogs";
import { useUI } from "../stores/useUI";
import { useDevSettings } from "../stores/useDevSettings";
import { IsProduction } from "../utils/client";
import NewCharacterModel from "./NewCharacterModel";

export default function Game({ isWarmingUp }: { isWarmingUp: boolean }) {
  const currentMap = useUI((state) => state.currentMap);
  const initializeLoadingTracker = useUI((state) => state.initializeLoadingTracker);
  const resetLoadingTracker = useUI((state) => state.resetLoadingTracker);
  const startGame = useUI((state) => state.startGame);
  const initializeGame = useLevelProgress((state) => state.initializeGame);
  const dialog = useDialogs((state) => state.dialog);
  const showGrid = useDevSettings((state) => state.showGrid);
  const isMouseDownForMoveToPoint = useGame((state) => state.isMouseDownForMoveToPoint);

  useEffect(() => {
    initializeGame();
    // Initialize loading tracker
    initializeLoadingTracker();
    return () => {
      resetLoadingTracker();
    };
  }, [initializeGame]);

  const handleMapComplete = () => {
    // This will be called when all NPCs in a map are completed
    // The progress is automatically updated in the store
  };

  // Debug settings
  const { physics, disableControl, disableFollowCam } = useControls("World Settings", {
    physics: false,
    disableControl: false,
    disableFollowCam: false
  });

  // Keyboard control preset
  const keyboardMap = [
    { name: "forward", keys: ["ArrowUp", "KeyW"] },
    { name: "backward", keys: ["ArrowDown", "KeyS"] },
    { name: "leftward", keys: ["ArrowLeft", "KeyA"] },
    { name: "rightward", keys: ["ArrowRight", "KeyD"] },
    { name: "jump", keys: ["Space"] },
    { name: "run", keys: ["Shift"] },
    { name: "action1", keys: ["1"] },
    { name: "action2", keys: ["2"] },
    { name: "action3", keys: ["3"] },
    { name: "action4", keys: ["KeyF"] },
  ];

  if (!currentMap) {
    return null;
  }

  return (
    <>
        {!IsProduction && <Perf position="bottom-left" />}
        {/* Grid */}
        {showGrid && (
          <Grid
            args={[300, 300]}
            sectionColor={"lightgray"}
            cellColor={"gray"}
            position={[0, -0.99, 0]}
            userData={{ camExcludeCollision: true }}
          />
        )}

        {/* Physics World */}
        <Physics debug={physics} timeStep="vary" paused={!startGame || dialog.isOpen} interpolate={true}>
          {/* Keyboard Controls */}
          <KeyboardControls map={keyboardMap}>
            {/* Character Control */}
            {!isWarmingUp && <Ecctrl
              debug
              animated={true}
              followLight
              springK={1.2}
              dampingC={0.08}
              floatHeight={0.1}
              autoBalanceSpringK={1.2}
              autoBalanceDampingC={0.04}
              autoBalanceSpringOnY={0.7}
              autoBalanceDampingOnY={0.05}
              camInitDis={-7}
              camLowLimit={-0.1}
              camUpLimit={1.5}
              camInitDir={{ x: Math.PI / 7, y: 0 }}
              camLerpMult={isMouseDownForMoveToPoint ? 1 : 10}
              camCollision={true}
              disableFollowCam={dialog.isOpen || isWarmingUp ? true : disableFollowCam}
              position={[0, 0, 0]} //disabled fall animation
              mode="FixedCamera PointToMove"
              disableControl={dialog.isOpen || isWarmingUp ? true : disableControl}
              jumpForceToGroundMult={5}
              jumpVel={4}
            >
              {/* <CharacterModel /> */}
              <NewCharacterModel />
            </Ecctrl>}
          </KeyboardControls>

          {/* Map Renderer */}
          <MapRenderer 
            mapId={currentMap}
            onComplete={handleMapComplete}
          />
        </Physics>
    </>
  );
}
